import { Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CustomModal from '../../../components/Common/CustomModal';
import { SubscriptionPlanTypes } from '../../../types/coach';
import PaymentInfo from '../../../components/Onboarding/PaymentInfo';
import SubscriptionPlanCard from '../../../components/Onboarding/SubscriptionPlanCard';
import {
  ENABLE_FREE_PLAN,
  TEnableFreePlanReq,
  TEnableFreePlanRes,
} from '../../../service/graphql/subscriptionQuery';
import { toastStyles } from '../../../utils/utils';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  activateFreePlanSuccess,
  createCheckoutSession,
} from '../../../redux/subscription/subscriptionSlice';
import { fetchCoachRequest } from '../../../redux/coach/coachSlice';
import { CHECKOUT_PAGE_ROUTE } from '../../../utils/routes';
import { onboardingActions } from '../../../redux/onboarding/onboardingSlice';

export interface IPlanInfoPopupProps {
  isOpen: boolean;
}

const PlanInfoPopup = ({ isOpen }: IPlanInfoPopupProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: coachId } = useAppSelector((state) => state.coach.coach);
  const [enableFreePlan, { data, error, loading, called }] = useMutation<
    TEnableFreePlanRes,
    TEnableFreePlanReq
  >(ENABLE_FREE_PLAN);
  const [moreInfoPlan, setMoreInfoPlan] = useState<SubscriptionPlanTypes | null>(null);

  const onMoreInfoDismiss = () => {
    setMoreInfoPlan(null);
  };
  const onMoreInfoSelect = (plan: SubscriptionPlanTypes) => {
    setMoreInfoPlan(plan);
  };

  const onPurchase = (plan: SubscriptionPlanTypes) => {
    if (plan === SubscriptionPlanTypes.FREE) {
      enableFreePlan({ variables: { coachId } });
    } else {
      dispatch(
        createCheckoutSession({
          coachId,
          planType: plan,
        })
      );
      dispatch(onboardingActions.openOnboardingSuccessModal());
      navigate(CHECKOUT_PAGE_ROUTE);
    }
  };

  useEffect(() => {
    if (!called || loading) return;
    if (!data?.activateFreePlan?.success || error) {
      const errMsg =
        data?.activateFreePlan?.message || error?.message || t('settings.requestError');
      toast.error(errMsg, toastStyles.error);
      return;
    }
    if (data?.activateFreePlan?.success && data?.activateFreePlan.message) {
      toast.success(data?.activateFreePlan?.message, toastStyles.success);
      dispatch(activateFreePlanSuccess());
      dispatch(onboardingActions.openOnboardingSuccessModal());
      dispatch(fetchCoachRequest());
    }
  }, [data, error]);
  return (
    <CustomModal xs={10.5} md={8.5} lg={10} isOpen={isOpen}>
      <Grid
        container
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        py={3}
        px={0.5}
        sx={{
          background: 'var(--Web-Background, linear-gradient(180deg, #FFF 0%, #FFEEE7 100%))',
        }}
        borderRadius="10px"
        maxHeight="95%"
        minHeight="80vh"
      >
        {moreInfoPlan ? (
          <PaymentInfo // subscription plan detailed info popup
            plan={moreInfoPlan}
            dismiss={onMoreInfoDismiss}
          />
        ) : (
          <SubscriptionPlanCard // plan list popup
            variant="title2Bold"
            xs={6}
            handleSelectPlan={onPurchase}
            onMoreInfoSelect={onMoreInfoSelect}
          />
        )}
      </Grid>
    </CustomModal>
  );
};

export default PlanInfoPopup;
